import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '@/lib/api';
import { PERSONAL_ASSISTANT_UPDATED_EVENT } from '@/lib/personal-assistant-events';
import type {
  PaConnector,
  PaConnectorsMode,
  PaConnectorsPerimeterResponse,
  PaConversationConnectorsResponse,
  PaConversationConnectorsSetRequest,
} from '@easyflow/shared';

// Task #3818 — Stato condiviso dei connettori PA (sidebar + menu composer).
//
// MODELLO DI AUTORITÀ: il perimetro effettivo arriva dal server (poteri agente
// + modalità delega/intersezione, decisi dall'admin). Qui si può SOLO
// restringere la selezione per-conversazione (`enabled`): null = default,
// tutto il perimetro attivo. Il server rifiuta (403) id fuori perimetro.

export interface UsePaConnectorsResult {
  connectors: PaConnector[];
  mode: PaConnectorsMode | null;
  /** Selezione per-chat (null = nessuna restrizione, tutto attivo). */
  enabled: string[] | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  /** True se il connettore è attivo nella conversazione corrente. */
  isActive: (connectorId: string) => boolean;
  /** N. connettori spenti nella conversazione corrente (0 = default). */
  restrictedCount: number;
  /** Accende/spegne un connettore della chat corrente (entro il perimetro). */
  toggle: (connectorId: string) => Promise<void>;
  /** Rimuove la restrizione (torna al perimetro pieno). */
  reset: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function usePaConnectors(assistantId: string, conversationId: string | null): UsePaConnectorsResult {
  const [connectors, setConnectors] = useState<PaConnector[]>([]);
  const [mode, setMode] = useState<PaConnectorsMode | null>(null);
  const [enabled, setEnabled] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);

  const refresh = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    setError(null);
    try {
      const perimeter = await apiClient.get<PaConnectorsPerimeterResponse>(
        `/personal-assistant/workspace/connectors?assistantId=${encodeURIComponent(assistantId)}`,
      );
      if (request !== generation.current) return;
      setConnectors(perimeter.data?.connectors ?? []);
      setMode(perimeter.data?.mode ?? null);
      if (conversationId) {
        const sel = await apiClient.get<PaConversationConnectorsResponse>(
          `/personal-assistant/workspace/connectors/conversations/${conversationId}`,
        );
        if (request !== generation.current) return;
        setEnabled(sel.data?.enabled ?? null);
      } else {
        setEnabled(null);
      }
    } catch (err) {
      if (request !== generation.current) return;
      console.warn('[usePaConnectors] load failed', err);
      setError('Caricamento connettori non riuscito');
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, [assistantId, conversationId]);

  useEffect(() => {
    setConnectors([]);
    setEnabled(null);
    void refresh();
    const updated = (event: WindowEventMap[typeof PERSONAL_ASSISTANT_UPDATED_EVENT]) => {
      if (!event.detail.assistantId || event.detail.assistantId === assistantId) void refresh();
    };
    window.addEventListener(PERSONAL_ASSISTANT_UPDATED_EVENT, updated);
    return () => {
      generation.current += 1;
      window.removeEventListener(PERSONAL_ASSISTANT_UPDATED_EVENT, updated);
    };
  }, [refresh, assistantId]);

  const isActive = useCallback(
    (connectorId: string) => enabled === null || enabled.includes(connectorId),
    [enabled],
  );

  const restrictedCount = useMemo(() => {
    if (enabled === null) return 0;
    const enabledSet = new Set(enabled);
    return connectors.filter(c => !enabledSet.has(c.id)).length;
  }, [enabled, connectors]);

  const persist = useCallback(async (next: string[] | null) => {
    if (!conversationId || loading || saving) return;
    const request = generation.current;
    setSaving(true);
    setError(null);
    const prev = enabled;
    setEnabled(next); // ottimistico: rollback su errore
    try {
      const body: PaConversationConnectorsSetRequest = { enabled: next };
      const res = await apiClient.put<PaConversationConnectorsResponse>(
        `/personal-assistant/workspace/connectors/conversations/${conversationId}`,
        body,
      );
      if (request !== generation.current) return;
      setEnabled(res.data?.enabled ?? null);
    } catch (err) {
      if (request !== generation.current) return;
      console.warn('[usePaConnectors] save failed', err);
      setEnabled(prev);
      setError(err instanceof Error ? err.message : 'Salvataggio non riuscito');
    } finally {
      setSaving(false);
    }
  }, [conversationId, enabled, loading, saving]);

  const toggle = useCallback(async (connectorId: string) => {
    const allIds = connectors.map(c => c.id);
    const current = enabled === null ? allIds : enabled;
    const next = current.includes(connectorId)
      ? current.filter(id => id !== connectorId)
      : [...current, connectorId];
    // Se la selezione torna a coprire tutto il perimetro → null (default).
    const coversAll = allIds.every(id => next.includes(id));
    await persist(coversAll ? null : next);
  }, [connectors, enabled, persist]);

  const reset = useCallback(async () => { await persist(null); }, [persist]);

  return { connectors, mode, enabled, loading, saving, error, isActive, restrictedCount, toggle, reset, refresh };
}
